import { Component, inject, OnDestroy } from '@angular/core';
import { OrderWrapperComponent } from "../../component/wrapper/order-wrapper.component";
import { Store } from '@ngrx/store';
import { Base } from '../../component/wrapper/base-wrapper.component';
import { ordersFeature, OrdersActions } from '../../store/order.store';
import { debounceTime, filter, lastValueFrom, Subject, take, takeUntil } from 'rxjs';
import { accountFeature } from '../../store/account.store';
import { loadStripe, Stripe } from '@stripe/stripe-js';
import { environment } from '../../../environments/environment';
import { OptionData, OrderPublic } from '../../../../globalTypes/order.types';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { getOrder, purchasePageOrders, PurchasePageOrdersVariables, sendMessage, SendMessageVariables } from '@dataconnect/account';
import { PurchaseOrderEntryComponent } from './purchase-order-entry.component';
import { getFormattedOrder } from '../../../../globalTypes/_helpers';
import { Logger } from '../../helper/logger';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PurchaseOrderDetailsComponent } from "./purchase-order-details.component";
import { Fulfillment, FulfillmentStatusType } from '../../../../globalTypes/fulfillment.types';
import { OrderService } from '../../services/order.service';
import { LocationPublic } from '../../../../globalTypes/account.types';
import { sortByKey } from '../../helper/general';
import { ButtonDisplay } from '../../component/layout/element/button.component';
import { MessageData, MessageDialogComponent } from './message.dialog';
import { Status } from '../../../../globalTypes/base.types';
import { AccountService } from '../../services/account.service';
import { MatDialog } from '@angular/material/dialog';
import { httpsCallable, HttpsCallableResult } from 'firebase/functions';
import { Functions } from '@angular/fire/functions';
import { uiErrorCatcher } from '../../helper/error.handling';
import { InteractionsService } from '../../services/interactions.service';
import { interactionsFeature } from '../../store/interactions.store';
import { LoadingLogoComponent } from "../../component/layout/element/loading-logo.component";
import { onCallFunctions } from '../../../../globalTypes/backend.api.types';
import { createBlankReview, formatFulfillmentForReviewObject, ReviewForEdit } from '../../../../globalTypes/review.types';
import { v4 as uuidV4 } from 'uuid';
import { EditProductReviewDialogComponent } from '../../component/review/edit.review.dialog';
import { ViewProductReviewDialogComponent } from '../../component/review/view.review.dialog';
const log = new Logger("PurchaseComponent")

export interface OrderSpecificReviews {
  buyerReview: ReviewForEdit | null;
  sellerReview: ReviewForEdit | null;
}
@Component({
  selector: 'msh-purchase',
  imports: [OrderWrapperComponent, PurchaseOrderEntryComponent, PurchaseOrderDetailsComponent, LoadingLogoComponent],
  template: `
  <msh-order-wrapper
    [order]="order"
    [fulfillmentStatusMap]="fulfillmentStatusMap"
  >
    @if(successMessage){
      <div class="text-center my-7 font-bold" >{{successMessage}}</div>
    }
    <div class=" m-auto flex justify-center ">
      @if(!initComplete){
        <msh-loading-logo class=" w-96 h-96"></msh-loading-logo>
      }@else if(!order){
       @if(!orders || orders.length === 0) { <div class="null">There are no pending orders for this location</div> } @else {
        <div>
          @for(order of orders; track order.id){
            <msh-purchase-order-entry 
              [ctaMap]="ctaMap"
              [takingAction]="takingAction"
              [buyerReviews]="buyerReviews"
              [sellerReviews]="sellerReviews"
              [fulfillmentStatusMap]="fulfillmentStatusMap"
              (onActionTaken)="actionTaken($event, order)"
              [order]="order"
            ></msh-purchase-order-entry>
          }
        </div>
       }
      } @else {
        <msh-purchase-order-details 
          [fulfillmentStatusMap]="fulfillmentStatusMap" 
          [ctaMap]="ctaMap" 
          [takingAction]="takingAction"
          [buyerReviews]="buyerReviews"
          [sellerReviews]="sellerReviews"
          (onOpenReviewDialog)="openReviewDialog($event)"
          class=" block w-full ml-0 md:ml-20" 
          (onActionTaken)="actionTaken($event, order)" 
          (onViewDownloadFiles)="viewDownloadFiles($event)"
          [order]="order"
        ></msh-purchase-order-details>
      }
    </div>
  </msh-order-wrapper>
  `,
  styles: ``
})
export class PurchaseComponent extends Base<PurchaseComponent> implements OnDestroy {

  orderService = inject(OrderService);
  focusedOrderId?: string
  location?: LocationPublic
  locations: {[locationId: string]: LocationPublic} = {}
  initComplete = false;
  takingAction = false;
  successMessage = '';
  stripe: Stripe | null = null;
  pageSetup$: Subject<void> = new Subject<void>();
  startedMarkingSeenInteractions = false;
  order?: OrderPublic
  orders: OrderPublic[] = [];
  moreOrdersAvailable = false;
  buyerReviews: { [fulfillmentId: string]: ReviewForEdit } = {};
  sellerReviews: { [fulfillmentId: string]: ReviewForEdit } = {};
  fulfillmentStatusMap: {[statusId: string]: Status} = {}
  standardCtaOptions = [
    {cta: "Send Message", fn: (ev: MouseEvent, optionData: OptionData) => this.orderService.sendMessageDialog(optionData, false)},
    {cta: "Report Issue", fn: (ev: MouseEvent, optionData: OptionData) => this.orderService.sendMessageDialog(optionData, true) },
  ]
  ctaMap: {[status in FulfillmentStatusType]?: ButtonDisplay } = {
    required: { cta: "Cancel & Refund", stealthMode: true, options: [...this.standardCtaOptions]},
    waiting: { cta: "Cancel & Refund", stealthMode: true, options: [...this.standardCtaOptions]},
    delinquency: { cta: "Cancel & Refund", stealthMode: true, options: [...this.standardCtaOptions]},
    terminated: { cta: "Archive", options: []},
    fulfilling: { cta: "Cancel & Request Refund", stealthMode: true, options: [...this.standardCtaOptions]},
    shipping: { cta: "Mark As Arrived & Correct", options: [this.makeCancelRefundCta(), ...this.standardCtaOptions, ]},
    delayed: { cta: "Cancel Shipment", stealthMode: true, options: [...this.standardCtaOptions]},
    incident: { cta: "Request Refund", options: [...this.standardCtaOptions]},
    arrived: { cta: "Write Review", stealthMode: true, options: []},
    downloadable: { cta: "Write Review", options: [
      {cta: "View Download Files", fn: (ev: MouseEvent, optionData: OptionData) => this.viewDownloadFiles(optionData.fulfillment.fulfillmentNumber)}
    ]},
    review: { cta: "Write Review", options: []},
  }

  constructor(
    private store: Store,
    private accountService: AccountService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private route: ActivatedRoute,
    private router: Router,
    private interactionsService: InteractionsService,
    private functions: Functions,
  ){
    super();
    this.router.events.pipe(takeUntil(this.$destroy)).subscribe((event) => {
      if (event instanceof NavigationEnd) {  
        if(this.route.snapshot.params['orderId']) {
          this.focusedOrderId = this.route.snapshot.params['orderId']
          this.startedMarkingSeenInteractions = false;
          this.setPageForOneOrder();
        } else {
          this.store.select(accountFeature.selectLocation).pipe(filter(l=>!!l), takeUntil(this.$destroy)).subscribe(async (location) =>{
            if(!location) throw new Error("No location");
            this.location = location
            this.setPageForManyOrders(location.id);
          })
        } 
      }
    })

    this.store.select(accountFeature.selectLocations).pipe(takeUntil(this.$destroy)).subscribe(async locations => {
      this.locations = locations as {[locationId:string]: LocationPublic}
      const statuses = [...await this.accountService.getStatuses('fulfillments'), ...await this.accountService.getStatuses('refunds')];
      this.fulfillmentStatusMap = statuses.reduce((a,s,i)=>{a[s.id]=s; return a;},{} as {[statusId: string]: Status})
      console.log({fulfillmentStatusMap: this.fulfillmentStatusMap})
    })

    
    this.store.select(interactionsFeature.selectInteractions).pipe(takeUntil(this.$destroy)).subscribe(interactions => {
      const needsRefreshedContent = interactions.some(i => this.orders.some(o => o.fulfillments_on_order.some(f => f.id === i?.fulfillment.id)))
      if(needsRefreshedContent){
        if(this.focusedOrderId){
          this.setPageForOneOrder();
        }else{
          this.setPageForManyOrders(this.location?.id as string);
        }
      }
    })
  }

  async setPageForOneOrder(){
    if(!this.focusedOrderId) return;
    const orderId: string = this.focusedOrderId;
    this.store.select(accountFeature.selectLocation).pipe(takeUntil(this.$destroy), takeUntil(this.pageSetup$)).subscribe( async (location) => {
      if(!location) return;
      this.location = location;
      try{
        this.stripe = await loadStripe(environment.stripe.key);
        await this.checkStatus();  
      } catch(error){
        log.error(error)
      }
      const orders = await lastValueFrom(this.store.select(ordersFeature.selectOrders).pipe(take(1)))
      let order = orders.find(order => order && order.id === orderId)
      if(!order) {
        const orderRef = await getOrder({orderId, locationId: location.id})
        order = orderRef.data.orders[0] as unknown as OrderPublic;
      };
      this.order = getFormattedOrder(order, 'fulfillment');
      this.order.fulfillments_on_order.forEach(async fulfillment => {
        this.setReviews(fulfillment);
      })
      this.orders = [this.order];
      this.initComplete = true;
      this.startMarkingSeenInteractions();
    })
  }

  async startMarkingSeenInteractions(){
    if(this.startedMarkingSeenInteractions) return;
    this.startedMarkingSeenInteractions = true;
    if(!this.order || !this.order.orderItems_by_fulfillment) return;
    const fulfillmentIds: string[] = Object.keys(this.order.orderItems_by_fulfillment)
    if(!fulfillmentIds.length || !this.location || !this.location.id) return;
    const locationId = this.location.id;
    this.store.select(interactionsFeature.selectEntities).pipe(debounceTime(800), takeUntil(this.$destroy), takeUntil(this.pageSetup$)).subscribe((interactions) => {
      const seenInteractionIds: string[] = [];
      Object.values(interactions).forEach(interaction => {
        if(interaction 
          && !interaction.sellerViewed
          && interaction.fulfillment.id 
          && fulfillmentIds.includes(interaction.fulfillment.id)
        ){
          seenInteractionIds.push(interaction.id);
        }
      })
      console.log({seenInteractionIds, interactions})
      this.interactionsService.markInteractionsAsSeen(seenInteractionIds, locationId, "Buyer");
      this.pageSetup$.next();
    });
  }

  async setPageForManyOrders(locationId: string){
    // TODO: set up hydration monitor sync
    // this.store.select(ordersFeature.selectOrders).pipe(takeUntil(this.$destroy)).subscribe(orders => {
    //   this.orders = orders as OrderPublic[]
    // })
    // query orders!
    const limit = 10
    const offset = 0;
    const orderVars: PurchasePageOrdersVariables = { locationId, limit, offset}
    log.log({orderVars})
    // first load from store,
    const orders = await lastValueFrom(this.store.select(ordersFeature.selectOrders).pipe(take(1)))
    this.orders = orders as OrderPublic[];
    // then pull from db
    // other pulls should be as a result of filtering or sorting actions
    log.log({orders: this.orders, areNotOrders: !this.orders.length})
    if(!this.orders.length){
      try {
        const orderRef = await purchasePageOrders(orderVars); 
        const orders = orderRef.data.orders as unknown as OrderPublic[]
        log.log({orders: orders});
        this.orders = orders;
        this.store.dispatch(OrdersActions.ordersLoaded({orders}))
        // save to store as map
      } catch(error: any){
        log.error(error);
        this.snackBar.open(error.message, "close", {duration: 30 * 1000})
      }
    }
    this.orders = this.orders.map(order=>getFormattedOrder(order, "fulfillment"));
    this.orders.sort(sortByKey('createdAt',-1))
    this.orders.forEach(order => {
      order.fulfillments_on_order.forEach(async fulfillment => {
        this.setReviews(fulfillment);
      })
    })
    this.initComplete = true;
    this.pageSetup$.next();
  }

  setReviews(fulfillment: Fulfillment){
    if(fulfillment.reviews_on_fulfillment){
      fulfillment.reviews_on_fulfillment.forEach(review => {
        review.fulfillment = formatFulfillmentForReviewObject(fulfillment);
        if(review.isBuyer){ this.buyerReviews[fulfillment.id] = review; } 
        else { this.sellerReviews[fulfillment.id] = review; }
      })
    }
  }

  viewDownloadFiles(fulfillmentNumber: number){
    this.router.navigate(['/order/digital-material'], { queryParams: { fulfillment: fulfillmentNumber } });
  }

  // Fetches the payment intent status after payment submission
  checkStatus = async () => {
    const clientSecret = new URLSearchParams(window.location.search).get(
      "payment_intent_client_secret"
    );

    if (!clientSecret || !this.stripe) {
      return;
    }

    const { paymentIntent } = await this.stripe.retrievePaymentIntent(clientSecret);
    if(!paymentIntent) return;
    switch (paymentIntent.status) {
      case "succeeded":
        this.successMessage = ("Payment successful!");
        // TODO  reload cart because it should be updating... or... maybe there should be a sync set up
        break;
      case "processing":
        this.successMessage = ("Your payment is processing.");
        break;
      case "requires_payment_method":
        this.successMessage = ("Your payment was not successful, please try again.");
        break;
      default:
        this.successMessage = ("Something went wrong.");
        break;
    }
  }

  makeCancelRefundCta() {
    return {cta: "Request Refund", fn: (ev: MouseEvent, optionData: OptionData) => {
      this.orderService.startRefund({fulfillment: optionData.fulfillment, 
        restockItems: false, 
        order: optionData.order,
      }, true)}
    }
  }

  async actionTaken(fulfillment: Fulfillment, order: OrderPublic){
    const statusId: FulfillmentStatusType = fulfillment.statusId
    const fulfillmentId: string = fulfillment.id;
    if(!this.location) return;
    console.log({actionTaken: fulfillment, statusId})
    const persistingOrderItems = (order.orderItems_by_fulfillment ? order.orderItems_by_fulfillment[fulfillmentId] : []).filter((item => !item.refundId))
    switch(statusId){
      case "required":
        this.takingAction = true;
        const optionData: OptionData = {fulfillment, 
          restockItems: true, 
          order
        }
        await this.orderService.startRefund(optionData, true)
        this.takingAction = false;
        break;
      case "waiting":
        break;
      case "shipping":
        this.takingAction = true;
        const callable = httpsCallable(this.functions,  onCallFunctions.orderCompleteTransfer);
        try {
          const results: HttpsCallableResult = await uiErrorCatcher(callable({fulfillmentId, locationId: fulfillment.fulfilledToLocationId}), this.snackBar, "Error transferring funds after fulfillment completion") 
          console.log({results})
        } catch(error){
          log.error("Error transferring funds after fulfillment completion", error)
        }
        this.takingAction = false; 
        break;
      case "downloadable":
      case "review":
        this.openReviewDialog(this.buyerReviews[fulfillment.id], fulfillment)
        break;
    }
    if(this.focusedOrderId){
      this.setPageForOneOrder();
    } else {
      this.setPageForManyOrders(this.location.id)
    }
  }

  async openReviewDialog(review?: ReviewForEdit, fulfillment?: Fulfillment){
    if(!review && fulfillment){
      const id = uuidV4()
      review = createBlankReview(id, fulfillment)
    }
    if(!review) return;
    const dialogComponent = review.isBuyer ? EditProductReviewDialogComponent : ViewProductReviewDialogComponent;
    const dialog = this.dialog.open(dialogComponent, {data: {review}});
    const updatedReview = await lastValueFrom(dialog.afterClosed().pipe(take(1)));
    if(updatedReview){
      const callable = httpsCallable(this.functions,  onCallFunctions.submitReview);
      try {
        const results: HttpsCallableResult = await uiErrorCatcher(callable({review: updatedReview}), this.snackBar, "Error submitting review") 
        console.log({results})
        // change fulfillment status to something
        this.snackBar.open("Review submitted successfully", "close", {duration: 3000});
      } catch(error){
        log.error("Error submitting review", error)
      }
    }
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.pageSetup$.next();
    this.pageSetup$.complete();
  }
}
